import { Trans } from "react-i18next";import { useState, useRef, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../../lib/utils";

/**
 * Shared tree component — used in two places:
 *   1. AssignTenantAccessPage.jsx — mode="select"
 *   2. TenantRegistryPage.jsx (Services & Modules Catalog tab) — mode="display"
 *
 * In "select" mode every node (service, module, sub-module, and leaf
 * feature) renders its own checkbox. Checking/unchecking a parent
 * cascades to every feature underneath it; a parent shows an
 * indeterminate state when only some of its children are selected.
 * `selectedServiceIds` is always a flat list of leaf feature ids — that's
 * the only thing the caller needs to track and persist.
 */
export default function AccessTree({
  data = [],
  mode = "display",
  selectedServiceIds = [],
  onChange,
  defaultExpanded = false
}) {
  const isSelectMode = mode === "select";
  const selectedSet = new Set(selectedServiceIds.map(String));

  if (!data.length) {
    return <p className="text-sm per-file-hint py-3"><Trans i18nKey="tenantManagement.noServicesAvailable" /></p>;
  }

  const getStatus = (ids) => {
    if (!ids || !ids.length) return "none";
    const selectedCount = ids.filter((id) => selectedSet.has(String(id))).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === ids.length) return "all";
    return "some";
  };

  const toggleIds = (ids, nextChecked) => {
    if (!isSelectMode) return;
    const next = new Set(selectedSet);
    (ids || []).forEach((id) => {
      const key = String(id);
      if (nextChecked) next.add(key);else
      next.delete(key);
    });
    onChange?.(Array.from(next));
  };

  return (
    <div className="flex flex-col gap-2.5">
      {data.map((service) =>
      <ServiceNode
        key={service.id}
        service={service}
        isSelectMode={isSelectMode}
        status={getStatus(service.serviceIds)}
        onToggle={(checked) => toggleIds(service.serviceIds, checked)}
        getStatus={getStatus}
        toggleIds={toggleIds}
        defaultExpanded={defaultExpanded} />

      )}
    </div>);

}

function TriCheckbox({ status, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = status === "some";
  }, [status]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={status === "all"}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      className="h-4 w-4 shrink-0 accent-accent cursor-pointer" />);


}

function Chevron({ expanded, disabled, onClick }) {
  return (
    <span
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center h-4 w-4 shrink-0 cursor-pointer transition-transform",
        disabled ? "invisible" : "visible",
        expanded && "rotate-90"
      )}>
      
      <ChevronRight className="h-3 w-3 per-file-hint" />
    </span>);

}

function ServiceNode({ service, isSelectMode, status, onToggle, getStatus, toggleIds, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasChildren = (service.modules || []).length > 0;

  const handleLabelClick = () => {
    if (hasChildren) setExpanded((e) => !e);else
    if (isSelectMode) onToggle(status !== "all");
  };

  return (
    <div className="border border-(--color-text-border) rounded-xl px-3.5 py-3 bg-white">
      <div className="flex items-center gap-2">
        <Chevron expanded={expanded} disabled={!hasChildren} onClick={() => hasChildren && setExpanded((e) => !e)} />

        {isSelectMode && <TriCheckbox status={status} onChange={onToggle} />}

        <span
          className={cn(
            "text-[14.5px] font-semibold text-foreground",
            (isSelectMode || hasChildren) && "cursor-pointer"
          )}
          onClick={handleLabelClick}>
          
          {service.name}
        </span>

        {hasChildren &&
        <span className="ml-auto text-[11.5px] font-semibold per-file-hint bg-workspace/60 rounded-full px-2.5 py-0.5">
            {service.modules.length} <Trans i18nKey="tenantManagement.module" />{service.modules.length !== 1 ? "s" : ""}
          </span>
        }
      </div>

      {expanded && hasChildren &&
      <div className="mt-2 ml-[22px] pl-3 border-l border-dashed border-(--color-text-border) flex flex-col gap-2">
          {service.modules.map((module) =>
        <ModuleNode
          key={module.id}
          module={module}
          isSelectMode={isSelectMode}
          status={getStatus(module.serviceIds)}
          onToggle={(checked) => toggleIds(module.serviceIds, checked)}
          getStatus={getStatus}
          toggleIds={toggleIds}
          defaultExpanded={defaultExpanded} />

        )}
        </div>
      }
    </div>);

}

function ModuleNode({ module, isSelectMode, status, onToggle, getStatus, toggleIds, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasChildren = (module.sub_modules || []).length > 0;

  const handleLabelClick = () => {
    if (hasChildren) setExpanded((e) => !e);else
    if (isSelectMode) onToggle(status !== "all");
  };

  return (
    <div className="mt-2 first:mt-0">
      <div className="flex items-center gap-2">
        <Chevron expanded={expanded} disabled={!hasChildren} onClick={() => hasChildren && setExpanded((e) => !e)} />
        {isSelectMode && <TriCheckbox status={status} onChange={onToggle} />}
        <span
          className={cn(
            "text-[13.5px] font-semibold text-foreground",
            (isSelectMode || hasChildren) && "cursor-pointer"
          )}
          onClick={handleLabelClick}>
          
          {module.name}
        </span>
      </div>

      {expanded && hasChildren &&
      <div className="mt-2 ml-[22px] pl-3 border-l border-dashed border-(--color-text-border) flex flex-col gap-2">
          {module.sub_modules.map((sub) =>
        <SubModuleNode
          key={sub.id}
          subModule={sub}
          isSelectMode={isSelectMode}
          status={getStatus(sub.serviceIds)}
          onToggle={(checked) => toggleIds(sub.serviceIds, checked)}
          getStatus={getStatus}
          toggleIds={toggleIds}
          defaultExpanded={defaultExpanded} />

        )}
        </div>
      }
    </div>);

}

function SubModuleNode({ subModule, isSelectMode, status, onToggle, getStatus, toggleIds, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const permissions = subModule.permissions || [];
  const hasChildren = permissions.length > 0;

  const handleLabelClick = () => {
    if (hasChildren) setExpanded((e) => !e);else
    if (isSelectMode) onToggle(status !== "all");
  };

  return (
    <div className="mt-2 first:mt-0">
      <div className="flex items-center gap-2">
        <Chevron expanded={expanded} disabled={!hasChildren} onClick={() => hasChildren && setExpanded((e) => !e)} />
        {isSelectMode && <TriCheckbox status={status} onChange={onToggle} />}
        <span
          className={cn(
            "text-[13px] font-medium per-file-hint",
            (isSelectMode || hasChildren) && "cursor-pointer"
          )}
          onClick={handleLabelClick}>
          
          {subModule.name}
        </span>
      </div>

      {expanded && hasChildren &&
      <div className="mt-2 ml-[22px] pl-3 border-l border-dashed border-(--color-text-border) flex flex-col gap-2">
          {permissions.map((perm) =>
        <PermissionNode
          key={perm.id}
          perm={perm}
          isSelectMode={isSelectMode}
          status={getStatus([perm.id])}
          onToggle={(checked) => toggleIds([perm.id], checked)} />

        )}
        </div>
      }
    </div>);

}

function PermissionNode({ perm, isSelectMode, status, onToggle }) {
  if (isSelectMode) {
    return (
      <label className="flex items-center gap-2 pl-1 cursor-pointer">
        <TriCheckbox status={status} onChange={onToggle} />
        <span className="text-[12.5px] per-file-hint">{perm.name} ({perm.id})</span>
      </label>);

  }

  return (
    <div className="flex items-center gap-2 pl-1">
      <span className="h-[5px] w-[5px] rounded-full bg-(--color-text-secondary) shrink-0" />
      <span className="text-[12.5px] per-file-hint">{perm.name} {(perm.id)}</span>
    </div>);

}
